import { useRef, useState } from "react";
import { Upload, Loader2, Check } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB — stays under proxy body limits

export default function FileUpload({ label, value, onChange, onBusy, accept = "image/*", testid }) {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const postChunk = async (file, uploadId, i, total) => {
    const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const form = new FormData();
    form.append("file", chunk, file.name);
    form.append("upload_id", uploadId);
    form.append("index", i);
    form.append("total", total);
    form.append("filename", file.name);
    form.append("file_size", file.size);
    let lastErr;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        return await api.post("/admin/upload-chunk", form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
        });
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        if ([400, 401, 403, 409, 422].includes(status)) throw err; // not worth retrying as-is
        await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
      }
    }
    throw lastErr;
  };

  const uploadChunked = async (file) => {
    const uploadId = crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    let result = null;
    for (let i = 0; i < total; i++) {
      let r;
      try {
        r = await postChunk(file, uploadId, i, total);
      } catch (err) {
        // The server tells us if any earlier chunk is missing: resend those, then the last one again.
        const missing = err?.response?.status === 409 && err.response.data?.detail?.missing;
        if (i === total - 1 && Array.isArray(missing) && missing.length) {
          for (const mi of missing) await postChunk(file, uploadId, mi, total);
          r = await postChunk(file, uploadId, i, total);
        } else {
          throw err;
        }
      }
      setProgress(Math.round(((i + 1) / total) * 100));
      if (r.data?.path) result = r.data;
    }
    if (result && result.size != null && result.size !== file.size) throw new Error("size mismatch");
    return result;
  };

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setProgress(0);
    onBusy?.(true);
    try {
      const data = await uploadChunked(file);
      if (!data?.path) throw new Error("no path");
      onChange(data.path);
      toast.success("File caricato");
    } catch (err) {
      toast.error("Upload fallito. Riprova con una connessione stabile.");
    } finally {
      onBusy?.(false);
      setLoading(false);
      setProgress(0);
      if (ref.current) ref.current.value = "";
    }
  };

  const isImage = accept.includes("image");

  return (
    <div>
      {label && <label className="block text-xs uppercase tracking-[0.15em] text-[#9C8F94] mb-2">{label}</label>}
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => ref.current?.click()} disabled={loading} data-testid={testid}
          className="inline-flex items-center gap-2 text-sm text-[#C6899E] border border-[rgba(198,137,158,0.3)] rounded-full px-4 py-2 hover:bg-[rgba(198,137,158,0.1)] transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : value ? <Check className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
          {loading ? `Caricamento… ${progress}%` : value ? "Sostituisci" : "Carica"}
        </button>
        {value && isImage && !loading && <img src={mediaUrl(value)} alt="" className="w-14 h-14 object-cover rounded-md border border-[rgba(61,50,56,0.1)]" />}
        {value && !isImage && !loading && <span className="text-xs text-[#9C8F94] truncate max-w-[200px]">{value.split("/").pop()}</span>}
      </div>
      {loading && (
        <div className="mt-2 h-1.5 w-full max-w-[280px] rounded-full bg-[rgba(198,137,158,0.15)] overflow-hidden">
          <div className="h-full bg-[#C6899E] transition-all duration-200" style={{ width: `${progress}%` }} />
        </div>
      )}
      <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" />
    </div>
  );
}
